<!-- eslint-disable vue/no-v-html -->
<template>
  <ClientOnly fallback-tag="span" fallback="Loading ...">
    <div class="mt-7 w-full xl:max-w-[650px]">
      <!-- Tabs title section-->
      <!-- overflow-hidden clips each tab's 1000%-wide base line at the strip's edge -->
      <div class="tabs relative z-[1] grid grid-cols-2 gap-4 overflow-hidden" role="tablist">
        <div v-for="(item, index) in membershipTabs" :key="index">
          <div class="flex py-3 hover:text-blue">
            <button
              :id="`${uid}-tab-${index}`"
              type="button"
              role="tab"
              :aria-selected="index === activeTab"
              :aria-controls="`${uid}-panel-${index}`"
              :tabindex="index === activeTab ? 0 : -1"
              class="relative block cursor-pointer font-sans text-base font-bold uppercase before:absolute before:-bottom-3 before:left-0 before:h-0.5 before:w-[1000%] before:bg-[#CAF7FF] before:content-[''] after:absolute after:-bottom-3 after:left-0 after:z-10 after:h-1 after:w-full after:content-[''] focus-visible:outline-offset-[-2px] lg:text-base"
              :class="[index === activeTab ? 'text-blue after:bg-blue' : 'text-black']"
              @click="activeTab = index"
              @keydown="onTabKeydown($event, index, i => (activeTab = i))"
            >
              {{ item }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tabs content section -->
      <TabPanels :active="activeTab" class="tabs-content mt-8">
        <!-- Tab 1 -->
        <div
          :id="`${uid}-panel-0`"
          class="px-6 lg:px-9"
          role="tabpanel"
          :aria-labelledby="`${uid}-tab-0`"
        >
          <h2
            class="mb-5 font-serif text-[22px] font-normal leading-tight text-black lg:mb-6 lg:text-28"
          >
            {{ $t('membershipTabs.animal-rights.title') }}
          </h2>
          <p class="mb-8 font-sans text-lg text-black lg:mb-8">
            {{ $t('membershipTabs.animal-rights.subtitle') }}
          </p>

          <div class="space-y-7 lg:max-w-[400px]">
            <RadioCardSimple
              v-for="option in categoryOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
              :description="option.description"
              class="!shadow-[0_2px_40px_0px_rgba(0,180,210,0.1)] lg:!shadow-[0_4px_30px_0px_rgba(0,180,210,0.2)]"
              v-model="modelMembershipType"
            />
            <div
              v-if="v$?.$invalid && v$?.$dirty && v$?.$errors?.length > 0"
              class="flex w-full justify-center rounded px-1 py-0 pr-3 font-sans text-sm text-pink-act"
              role="alert"
            >
              <span v-for="err in v$.$errors" :key="err.$uid">{{ err.$message }}</span>
            </div>
          </div>

          <div>
            <Button
              color="pink"
              class="mb-4 mt-10 w-full lg:w-[350px]"
              :title="t('button.next')"
              :arrow-right="true"
              :disabled="invalidCategory"
              @click="handleClickNext"
            />
            <p v-if="invalidCategory" class="text-pink">
              Den valda kategorin är ogiltig för din ålder. Välj en annan medlemskategori för att
              fortsätta.
            </p>
          </div>
        </div>

        <!-- Tab 2 -->
        <div
          :id="`${uid}-panel-1`"
          class="px-6 lg:px-9"
          role="tabpanel"
          :aria-labelledby="`${uid}-tab-1`"
        >
          <h2
            class="mb-5 font-serif text-[22px] font-normal leading-tight text-black lg:mb-6 lg:text-28"
          >
            {{ $t('membershipTabs.children.title') }}
          </h2>
          <p class="mb-5 font-sans text-lg text-black lg:mb-8">
            {{ $t('membershipTabs.children.subtitle') }}
          </p>

          <!-- <div class="space-y-7 lg:max-w-[350px]">
          <RadioCardSimple
            v-for="option in membershipList?.filter(item => item.type === 'RDK')"
            :key="option.value"
            :label="option.label"
            :value="option.value"
            :description="option.description"
            v-model="modelMembershipType"
          />
          <div
            v-if="v$?.$invalid && v$?.$dirty && v$?.$errors?.length > 0"
            class="flex w-full justify-center rounded px-1 py-0 pr-3 font-sans text-sm text-pink-act"
            role="alert"
          >
            <span v-for="err in v$.$errors" :key="err.$uid">{{ err.$message }}</span>
          </div>
        </div> -->
          <div class="text-blue">
            <p>Klicka på knappen nedan för att komma vidare till Rädda Djuren-klubbens hemsida.</p>
          </div>
          <Button
            color="pink"
            class="mb-4 mt-7 w-full lg:mt-10 lg:w-[350px]"
            :title="t('button.next')"
            :arrow-right="true"
            @click="handleRedirectToRdk"
          /></div
      ></TabPanels>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import type { Validation } from '@vuelidate/core'
import { useVModel } from '@vueuse/core'
import { useId } from 'vue'
import { Button, TabPanels, onTabKeydown } from 'refresh-ui'
import { useI18n } from 'vue-i18n'
import RadioCardSimple from '../atoms/RadioCardSimple.vue'
import { TYPE_MEMBERSHIP, type keyTypeMembership } from '../../types/membership'
import { getYearsOld } from '../../libs/age'
import { withQuery, type QueryObject, type QueryValue } from 'ufo'

const { t } = useI18n()
const route = useRoute()
const params = route.query

const props = defineProps<{
  person: any
  variants: string[]
  membershipType: keyTypeMembership
  v$: Validation
  rdkSlug?: string
}>()

const membershipTabs = ['Djurens Rätt', 'För barn']

const RDK_FORWARDED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
]

/** A repeated param arrives as an array; the campaign code is one value. */
const firstQueryValue = (value: QueryValue | QueryValue[]): QueryValue =>
  Array.isArray(value) ? value[0] : value

const age = computed(() => {
  if (!props.person.birthDate) return null
  return getYearsOld(props.person.birthDate)
})
const isChild = computed(() => {
  return age.value !== null && age.value <= 13
})

const uid = useId()
const activeTab = ref(isChild.value ? 1 : 0)

watch(isChild, newValue => {
  activeTab.value = newValue ? 1 : 0
})

const membershipList = ref([
  {
    label: 'Ordinarie medlemsavgift: 330 kr',
    value: TYPE_MEMBERSHIP.ORDINARY,
    description: 'Största stödet för djuren❤️ Var med och gör större skillnad för djuren.',
    type: 'Ordinarie',
  },
  {
    label: 'Reducerad medlemsavgift: 150 kr',
    value: TYPE_MEMBERSHIP.REDUCED,
    description: 'För dig som är 18-25 år, är pensionär eller har sjukersättning.',
    type: 'Reducerad',
  },
  {
    label: 'Fri medlemsavgift: 0 kr',
    value: TYPE_MEMBERSHIP.YOUTH,
    description: 'För dig som är 14-17 år och vill engagera dig för djuren.',
    type: 'Ungdom',
  },
])
const categoryOptions = membershipList.value.filter(item =>
  props.variants ? props.variants.includes(item.type) : true,
)

const emits = defineEmits(['update:membershipType', 'onNext'])
const modelMembershipType = useVModel(props, 'membershipType', emits)

onMounted(() => {
  if (categoryOptions.length > 0) {
    modelMembershipType.value = categoryOptions[0]?.value ?? TYPE_MEMBERSHIP.ORDINARY
  }
})

const invalidCategory = computed(() => {
  if (modelMembershipType.value !== TYPE_MEMBERSHIP.YOUTH) return
  if (!age.value) return true
  return age.value < 14 || age.value > 17
})

const handleClickNext = async () => {
  if (props.v$) {
    props.v$?.$touch()
    props.v$.$validate()
    if (!props.v$.$invalid) emits('onNext')
    return
  }
  emits('onNext')
}

const handleRedirectToRdk = () => {
  // check if rdkSlug starts with a slash, if not add one
  const slug = props.rdkSlug
    ? props.rdkSlug.startsWith('/')
      ? props.rdkSlug
      : `/${props.rdkSlug}`
    : ''

  // Only campaign and tracking params cross over: the pixels on raddadjuren.se record the full
  // URL, so personal details in the query end up at Meta and Google. The form is re-entered there.
  const queryParams: QueryObject = Object.fromEntries(
    Object.entries(params).filter(([key]) => RDK_FORWARDED_PARAMS.includes(key)),
  )

  // `kod` is the spelling on the RDK campaign links, but raddadjuren.se parses `zFormUrls` and
  // reads only `campaignCode` — forwarded as `kod` it is dropped there and the membership falls
  // back to the CMS recruitment code, losing the campaign. Normalise it; an explicit
  // `campaignCode` wins.
  const campaignCode = firstQueryValue(params.campaignCode) || firstQueryValue(params.kod)
  if (campaignCode) queryParams.campaignCode = campaignCode
  const redirectUrl = withQuery(
    `https://www.raddadjuren.se/medlem${slug}?step=child-details`,
    queryParams,
  )
  navigateTo(redirectUrl, { external: true, open: { target: '_blank' } })
}
</script>
